import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Loader2, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { Hint, SideCard, Steps, type PanelProps } from "./channel-wizard-kit";

/* Viber: вход в аккаунт по QR-коду с PIN-подтверждением.
   Телефон сканирует код, на нём подтверждают вход — пароль не нужен. */

const makeToken = () => Math.random().toString(36).slice(2, 10).toUpperCase();
const makePin = () => String(Math.floor(1000 + Math.random() * 9000));

export default function ViberLogin({ onDone }: PanelProps) {
  const [token, setToken] = useState(makeToken);
  const [pin, setPin] = useState(makePin);
  const [seconds, setSeconds] = useState(60);
  const [scan, setScan] = useState<"wait" | "scanned" | "linked">("wait");

  const qrValue = useMemo(() => `viber://pc?token=${token}&ref=hub`, [token]);

  const finish = useCallback(() => {
    onDone("QR · +7 916 402-18-73", [
      { label: t("Тип"), value: t("Viber · аккаунт") },
      { label: t("Способ входа"), value: t("QR-код / PIN") },
      { label: t("Право писать первым"), value: t("включено") },
      { label: t("История"), value: t("3 последних диалога") },
    ]);
  }, [onDone]);

  const rotate = useCallback(() => {
    setToken(makeToken());
    setPin(makePin());
    setSeconds(60);
    setScan("wait");
  }, []);

  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (seconds === 0) rotate();
  }, [seconds, rotate]);

  /* имитация сканирования телефона */
  useEffect(() => {
    const t1 = setTimeout(() => setScan("scanned"), 6500);
    const t2 = setTimeout(() => {
      setScan("linked");
      finish();
    }, 10500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [token, finish]);

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={2}
          done={scan === "linked"}
          items={[
            t("Откройте Viber на телефоне компании"),
            t("Настройки → Компьютер → Сканировать QR"),
            t("Наведите камеру на код или введите PIN справа"),
          ]}
        />

        <div className="mt-4 rounded-btn border border-border bg-background p-3">
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            {t("Подключается любой аккаунт Viber — с возможностью писать первым. Пароль и код из SMS не нужны: вход подтверждают на телефоне.")}
          </p>
          <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
            {t("После подключения новые переписки начнут сохраняться в CRM. Три последних диалога подтянутся сразу.")}
          </p>
        </div>

        {scan === "linked" ? (
          <div className="mt-3 rounded-btn border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5">
            <p className="flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700">
              <Check className="size-3.5" strokeWidth={3} aria-hidden />
              {t("Аккаунт привязан — можно отвечать")}
            </p>
          </div>
        ) : null}

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Аккаунт видит только администратор. Продавцы получают диалоги, а не доступы.")}
        </Hint>
      </div>

      {/* правая колонка: QR-код, PIN и статус */}
      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <div className="flex flex-col items-center">
          <div className="relative w-full max-w-[14rem] rounded-card border border-border bg-background p-2.5">
            <div className="aspect-square w-full overflow-hidden rounded-btn bg-white p-2">
              <QRCodeSVG
                value={qrValue}
                size={220}
                level="M"
                bgColor="#ffffff"
                fgColor="#111111"
                className="size-full"
              />
            </div>
            {scan === "scanned" ? (
              <div className="absolute inset-2.5 grid place-items-center rounded-btn bg-background/90 backdrop-blur-sm">
                <div className="text-center">
                  <span
                    className="mx-auto grid size-11 place-items-center rounded-full bg-emerald-500 text-white"
                    aria-hidden
                  >
                    <Smartphone className="size-5" />
                  </span>
                  <p className="mt-2 text-[12px] font-semibold">{t("Код отсканирован")}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{t("Подтвердите на телефоне")}</p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-2.5 flex w-full items-center justify-between gap-2 rounded-btn border border-border bg-background px-2.5 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              PIN
            </span>
            <span className="font-mono text-[15px] font-semibold tracking-[0.28em] text-foreground">{pin}</span>
          </div>

          <div className="mt-2 flex w-full items-center justify-between gap-2 rounded-btn border border-border bg-background px-2.5 py-1.5">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
              <Loader2 className="size-3 animate-spin text-primary" aria-hidden />
              {t("Ждём сканирование · {seconds} с", { seconds })}
            </span>
            <button
              type="button"
              onClick={rotate}
              className="inline-flex flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-1.5 py-1 text-[11px] font-semibold text-primary transition hover:bg-primary/10"
            >
              <RefreshCw className="size-3" aria-hidden />
              {t("Новый код")}
            </button>
          </div>

          <p className={cn("mt-1.5 break-all text-center font-mono text-[10px] leading-relaxed text-muted-foreground")}>
            {token}
          </p>
        </div>

        <SideCard title={t("Что будет после входа")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              t("Все новые переписки → в CRM"),
              t("Три последних диалога подтянутся сразу"),
              t("Можно писать клиенту первым"),
            ].map((item) => (
              <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}